import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  loginSuccess,
  logout as logoutAction,
} from '../store/slices/authSlice';
import { RootState } from '../store/store';
import { useIdleTimer } from '../hooks/useIdleTimer';
import { useNavigate } from 'react-router-dom';
import {
  signIn as apiSignIn,
  signOut as apiSignOut,
  returnOnboarding as apiReturnOnboarding,
} from '../service/authService';
import Loading from '@/components/landing/layout/Loader';
import { decodeJWT } from '../utils/jwt';
import ReturnChatLoader from '@/components/common/ReturnChatLoader';

interface AuthUser {
  name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  returnLogin: (orderId: string, mobile: string) => Promise<any>;
  loginWithToken: (token: string, type?: 'regular' | 'token') => { success: boolean; expired?: boolean };
  logout: () => Promise<void>;
  loginType: 'regular' | 'token' | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  login: async () => {},
  returnLogin: async () => null,
  loginWithToken: () => ({ success: false }),
  logout: async () => {},
  loginType: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, accessToken, loginType } = useSelector(
    (state: RootState) => state.auth
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useIdleTimer();

  useEffect(() => {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      localStorage.setItem('token', accessToken);
      if (loginType) localStorage.setItem('loginType', loginType);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
      localStorage.removeItem('loginType');
    }
  }, [accessToken, loginType]);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: signInError } = await apiSignIn(
          email,
          password
        );
        if (signInError) throw signInError;

        const {
          user: { name, email: userEmail, address, phone },
          token,
        } = data;

        dispatch(
          loginSuccess({
            user: {
              name,
              email: userEmail,
              phone: phone ?? null,
              address,
            },
            accessToken: token,
            loginType: 'regular',
          })
        );
        // no navigate here—caller will redirect

      } catch (err: any) {
        console.error('Login error:', err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const loginWithToken = useCallback(
    (token: string, type: 'regular' | 'token' = 'token') => {
      try {
        const payload = decodeJWT(token);
        if (!payload) return { success: false };

        // Check for expiration with a 60-second clock-drift buffer
        const now = Math.floor(Date.now() / 1000);
        const buffer = 60; // 60 seconds buffer
        if (payload.exp && payload.exp < (now - buffer)) {
          console.warn('Token has expired');
          return { success: false, expired: true };
        }

        dispatch(
          loginSuccess({
            user: {
              name: payload.name || payload.email.split('@')[0],
              email: payload.email,
              phone: payload.phone || null,
              address: payload.address || null,
            },
            accessToken: token,
            loginType: type,
          })
        );
        
        return { success: true };
      } catch (err) {
        console.error('Login with token error:', err);
        return { success: false };
      }
    },
    [dispatch]
  );

  const returnLogin = useCallback(
    async (orderId: string, mobile: string) => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: onboardingError } = await apiReturnOnboarding(
          orderId,
          mobile
        );
        if (onboardingError) throw onboardingError;

        const { token, ...restData } = data.data;

        if (token) {
          loginWithToken(token, 'token');
        }

        return restData;
      } catch (err: any) {
        console.error('Return Login error:', err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [loginWithToken]
  );

  const logout = useCallback(async () => {
    setLoading(true);
    const guestSlug = localStorage.getItem('merchant_portal_slug');
    const isGuest = loginType === 'token';

    try {
      await apiSignOut();
    } catch (err) {
      console.error('Sign-out error:', err);
    } finally {
      dispatch(logoutAction());
      // We don't necessarily clear merchant_portal_slug here so it can be reused 
      // if they want to log back into the same portal, but we could if strict isolation is needed.
      
      if (isGuest && guestSlug) {
        navigate(`/${guestSlug}/return`);
      } else {
        navigate('/customer-login');
      }
      setLoading(false);
    }
  }, [dispatch, navigate, loginType]);

  const isAuthenticated = Boolean(user);
  
  useEffect(() => {
    const restoreSession = async () => {
      const savedToken = localStorage.getItem('token');
      const savedLoginType = localStorage.getItem('loginType') as 'regular' | 'token' | null;
      
      if (savedToken && !accessToken) {
        loginWithToken(savedToken, savedLoginType || 'token');
      }
      setHydrated(true);
    };
    restoreSession();
  }, [accessToken, loginWithToken]);

  const authContextValue = useMemo(
    () => ({
      user,
      loading,
      error,
      isAuthenticated,
      login,
      returnLogin,
      loginWithToken,
      logout,
      loginType,
    }),
    [user, loading, error, isAuthenticated, login, returnLogin, loginWithToken, logout, loginType]
  );

  if (!hydrated) {
    const isReturnChat = window.location.pathname.toLowerCase().includes('/customer/return-chat');
    if (isReturnChat) {
      return <ReturnChatLoader />;
    }
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
